import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  VIDEO_TYPE_OPTIONS,
  getAssetUrl,
  getInitialFormValues,
  getInitialVideoMode,
  isManagedUploadPath,
  isVideoFileUrl,
} from './helpers';

const ReelVideoForm = ({
  mode = 'add',
  initialValues = null,
  loading = false,
  onSubmit,
  submitLabel,
  rowClassName = 'row mx-4',
  labelClassName = 'adminlables',
  buttonWrapperClassName = 'mt-4 text-right mx-4',
  buttonClassName = 'btn addbtn btn-lg',
}) => {
  const reel = initialValues || null;
  const [videoMode, setVideoMode] = useState('url');
  const [formData, setFormData] = useState(() => getInitialFormValues(initialValues || {}));
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState('');

  useEffect(() => {
    const nextValues = initialValues || {};
    setVideoMode(getInitialVideoMode(nextValues));
    setFormData(getInitialFormValues(nextValues));
  }, [initialValues]);

  useEffect(() => {
    if (!formData.video) {
      setVideoPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.video);
    setVideoPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.video]);

  useEffect(() => {
    if (!formData.thumbnailFile) {
      setThumbnailPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.thumbnailFile);
    setThumbnailPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.thumbnailFile]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'is_active' ? Number(value) : value,
    }));
  };

  const handleVideoModeChange = (event) => {
    const nextMode = event.target.value;
    setVideoMode(nextMode);
    setFormData((prevState) => ({
      ...prevState,
      video: null,
      video_url: nextMode === 'upload' ? '' : prevState.video_url,
      video_type: nextMode === 'upload' ? 'mp4' : prevState.video_type === 'mp4' ? 'youtube' : prevState.video_type,
    }));
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      video: file,
      video_type: 'mp4',
    }));
    setVideoMode('upload');
  };

  const handleThumbnailFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      thumbnailFile: file,
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast('Title is required.');
      return false;
    }

    if (videoMode === 'upload') {
      const canKeepExistingUpload = mode === 'update' && isManagedUploadPath(reel?.video_url);

      if (!formData.video && !canKeepExistingUpload) {
        toast('Please choose a video file.');
        return false;
      }
    }

    if (videoMode === 'url' && !formData.video_url.trim()) {
      toast('Video URL is required.');
      return false;
    }

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = new FormData();
    payload.append('title', formData.title.trim());
    payload.append('description', formData.description.trim());
    payload.append('is_active', String(formData.is_active));

    if (videoMode === 'upload') {
      if (formData.video) {
        payload.append('video', formData.video);
      }
    } else {
      payload.append('video_url', formData.video_url.trim());
      payload.append('video_type', formData.video_type);
    }

    if (formData.thumbnailFile) {
      payload.append('thumbnail', formData.thumbnailFile);
    } else if (formData.thumbnail_url.trim()) {
      payload.append('thumbnail', formData.thumbnail_url.trim());
    } else if (mode === 'update' && reel?.thumbnail && !isManagedUploadPath(reel.thumbnail)) {
      payload.append('thumbnail', '');
    }

    onSubmit(payload);
  };

  const currentVideoUrl =
    videoPreviewUrl ||
    (videoMode === 'upload'
      ? isManagedUploadPath(reel?.video_url)
        ? getAssetUrl(reel.video_url)
        : ''
      : formData.video_url.trim());

  const currentThumbnailUrl =
    thumbnailPreviewUrl ||
    (formData.thumbnail_url.trim()
      ? getAssetUrl(formData.thumbnail_url.trim())
      : isManagedUploadPath(reel?.thumbnail)
        ? getAssetUrl(reel.thumbnail)
        : '');

  return (
    <form onSubmit={handleSubmit}>
      <div className={rowClassName}>
        <div className="col-sm-6 mt-3">
          <label htmlFor="reelVideoTitle" className={labelClassName}>
            Title
          </label>
          <input
            id="reelVideoTitle"
            name="title"
            type="text"
            className="form-control"
            value={formData.title}
            onChange={handleChange}
          />
        </div>

        <div className="col-sm-6 mt-3">
          <label htmlFor="reelVideoStatus" className={labelClassName}>
            Status
          </label>
          <select
            id="reelVideoStatus"
            name="is_active"
            className="form-control"
            value={formData.is_active}
            onChange={handleChange}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        <div className="col-sm-12 mt-4">
          <label htmlFor="reelVideoDescription" className={labelClassName}>
            Description
          </label>
          <textarea
            id="reelVideoDescription"
            name="description"
            rows={4}
            className="form-control"
            value={formData.description}
            onChange={handleChange}
          />
        </div>

        <div className="col-sm-6 mt-4">
          <label htmlFor="reelVideoSource" className={labelClassName}>
            Video Source
          </label>
          <select
            id="reelVideoSource"
            className="form-control"
            value={videoMode}
            onChange={handleVideoModeChange}
          >
            <option value="upload">Upload File</option>
            <option value="url">External URL</option>
          </select>
        </div>

        {videoMode === 'url' && (
          <div className="col-sm-6 mt-4">
            <label htmlFor="reelVideoType" className={labelClassName}>
              Video Type
            </label>
            <select
              id="reelVideoType"
              name="video_type"
              className="form-control"
              value={formData.video_type}
              onChange={handleChange}
            >
              {VIDEO_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {videoMode === 'upload' ? (
          <div className="col-sm-6 mt-4">
            <label htmlFor="reelVideoFile" className={labelClassName}>
              Video File
            </label>
            <input
              id="reelVideoFile"
              type="file"
              name="video"
              className="form-control btn-block filebg"
              accept="video/mp4,video/webm,video/ogg"
              onChange={handleVideoFileChange}
            />
          </div>
        ) : (
          <div className="col-sm-12 mt-4">
            <label htmlFor="reelVideoUrl" className={labelClassName}>
              Video URL
            </label>
            <input
              id="reelVideoUrl"
              type="url"
              name="video_url"
              className="form-control"
              value={formData.video_url}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="col-sm-6 mt-4">
          <label htmlFor="reelVideoThumbnailFile" className={labelClassName}>
            Thumbnail File
          </label>
          <input
            id="reelVideoThumbnailFile"
            type="file"
            name="thumbnailFile"
            className="form-control btn-block filebg"
            accept="image/*"
            onChange={handleThumbnailFileChange}
          />
        </div>

        <div className="col-sm-6 mt-4">
          <label htmlFor="reelVideoThumbnailUrl" className={labelClassName}>
            Thumbnail URL
          </label>
          <input
            id="reelVideoThumbnailUrl"
            type="url"
            name="thumbnail_url"
            className="form-control"
            value={formData.thumbnail_url}
            onChange={handleChange}
          />
        </div>

        {currentVideoUrl && (
          <div className="col-sm-6 mt-4">
            <div className="adminlables">Current Video</div>
            {videoMode === 'upload' || isVideoFileUrl(currentVideoUrl) ? (
              <video controls className="w-100 rounded" style={{ maxHeight: 220 }}>
                <source src={currentVideoUrl} />
                <track kind="captions" src="" label="Reel video preview captions" />
                Your browser does not support video playback.
              </video>
            ) : (
              <a href={currentVideoUrl} target="_blank" rel="noreferrer" className="btn editbtn d-inline-block mt-2">
                Open Video
              </a>
            )}
          </div>
        )}

        {currentThumbnailUrl && (
          <div className="col-sm-6 mt-4">
            <div className="adminlables">Current Thumbnail</div>
            <div>
              <img
                src={currentThumbnailUrl}
                alt={formData.title || 'thumbnail'}
                className="img-thumbnail"
                style={{ maxWidth: 220, width: '100%', objectFit: 'cover' }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={buttonWrapperClassName}>
        <button type="submit" className={buttonClassName} disabled={loading}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ReelVideoForm;
